import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import PaymentModal from '../components/PaymentModal';
import api, { resolveBoletoChaveFromFatura } from '../config/api-config';

const fallbackFaturas = [
  {
    id: 1,
    referencia: 'Janeiro 2025',
    mes: 'Janeiro 2025',
    codigo: 'FAT001',
    numero: '',
    valor: 88.0,
    vencimento: '15/01/2025',
    status: 'Pago',
    dataPagamento: '10/01/2025',
    chaveBoleto: '',
  },
  {
    id: 2,
    referencia: 'Fevereiro 2025',
    mes: 'Fevereiro 2025',
    codigo: 'FAT002',
    numero: '',
    valor: 88.0,
    vencimento: '15/02/2025',
    status: 'Pago',
    dataPagamento: '12/02/2025',
    chaveBoleto: '',
  },
  {
    id: 3,
    referencia: 'Março 2025',
    mes: 'Março 2025',
    codigo: 'FAT003',
    numero: '395494',
    valor: 88.0,
    vencimento: '15/03/2025',
    status: 'Pendente',
    dataPagamento: null,
    chaveBoleto: '395494',
  },
];

function formatDateBr(raw) {
  if (raw == null || raw === '') return '—';
  if (typeof raw === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(raw.trim())) {
    return raw.trim();
  }
  const dt = new Date(raw);
  if (!Number.isNaN(dt.getTime())) {
    return dt.toLocaleDateString('pt-BR');
  }
  return String(raw);
}

function mapDisplayStatus(s) {
  const x = String(s || '').toLowerCase();
  if (x.includes('pag')) return 'Pago';
  if (x.includes('venc')) return 'Vencido';
  if (x.includes('pend') || x.includes('aberto') || x === '') return 'Pendente';
  return 'Pendente';
}

function mapApiBoletoToRow(raw, index) {
  const n = (x) => (x == null || x === '' ? '' : String(x).trim());
  const referencia =
    n(raw.referencia) ||
    n(raw.mesReferencia) ||
    n(raw.mes) ||
    n(raw.MesReferencia) ||
    n(raw.Mes) ||
    '—';
  const numero =
    n(raw.numero) ||
    n(raw.numeroBoleto) ||
    n(raw.Numero) ||
    n(raw.NossoNumero) ||
    n(raw.nossoNumero) ||
    '';
  const valor = Number(raw.valor ?? raw.valorTitulo ?? raw.Valor ?? 0);
  const venc = formatDateBr(raw.vencimento ?? raw.dataVencimento ?? raw.Vencimento);
  const pag = raw.dataPagamento ?? raw.data_pagamento ?? raw.DataPagamento;
  return {
    id: raw.id ?? raw.idBoleto ?? raw.Id ?? index,
    referencia,
    mes: referencia,
    codigo: n(raw.codigo) || numero || `—`,
    numero,
    valor,
    vencimento: venc,
    status: mapDisplayStatus(raw.status ?? raw.situacao ?? raw.Status),
    dataPagamento: pag ? formatDateBr(pag) : null,
    chaveBoleto: n(raw.chaveBoleto) || n(raw.chave) || numero,
  };
}

function Faturas() {
  const [faturas, setFaturas] = useState(fallbackFaturas);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const [selectedFatura, setSelectedFatura] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const loadFaturas = useCallback(async () => {
    try {
      const raw = localStorage.getItem('unet_user');
      if (!raw) {
        setLoading(false);
        return;
      }
      const user = JSON.parse(raw);
      const clienteId = user.idCliente ?? user.clienteId ?? user.id;
      if (clienteId == null) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setLoadError(false);
      const data = await api.getBoletos(clienteId);
      const list = Array.isArray(data) ? data : data?.boletos ?? data?.data ?? [];
      if (list.length > 0) {
        setFaturas(list.map(mapApiBoletoToRow));
      } else {
        setFaturas(fallbackFaturas);
      }
    } catch (e) {
      console.warn('Faturas: usando lista local (API indisponível ou sem dados)', e);
      setLoadError(true);
      setFaturas(fallbackFaturas);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFaturas();
  }, [loadFaturas]);

  const getStatusClass = (status, vencimento) => {
    const isVencido = () => {
      if (!vencimento || vencimento === '—') return false;
      const vencimentoDate = new Date(vencimento.split('/').reverse().join('-'));
      const hoje = new Date();
      return vencimentoDate < hoje;
    };

    switch (status.toLowerCase()) {
      case 'pago':
        return 'status-paid';
      case 'pendente':
        return isVencido() ? 'status-overdue' : 'status-pending';
      case 'vencido':
        return 'status-overdue';
      default:
        return 'status-default';
    }
  };

  const getStatusText = (status, vencimento) => {
    const isVencido = () => {
      if (!vencimento || vencimento === '—') return status;
      const vencimentoDate = new Date(vencimento.split('/').reverse().join('-'));
      const hoje = new Date();
      return vencimentoDate < hoje;
    };

    if (status.toLowerCase() === 'pendente' && isVencido()) {
      return 'Vencido';
    }
    if (status.toLowerCase() === 'pendente') {
      return 'A vencer';
    }
    return status;
  };

  const handlePaymentClick = (fatura) => {
    setSelectedFatura(fatura);
    setShowPaymentModal(true);
  };

  const handleDownloadBoleto = async (fatura) => {
    const chave = resolveBoletoChaveFromFatura(fatura);
    if (!chave) {
      alert(
        'Não há número/chave de boleto para esta fatura. Verifique se o boleto foi gerado no sistema.'
      );
      return;
    }
    try {
      await api.downloadBoletoPdf(chave);
    } catch (error) {
      console.error('Erro ao baixar boleto em PDF:', error);
      alert(
        error?.message ||
          'Não foi possível baixar o boleto em PDF no momento. Tente abrir em nova aba pelo link direto ou entre em contato com o suporte.'
      );
    }
  };

  const boletoKey = (f) => resolveBoletoChaveFromFatura(f);

  return (
    <div className="faturas-page">
      <div className="container">
        <div className="page-header">
          <h1>Minhas Faturas</h1>
          <p>Acompanhe suas faturas e histórico de pagamentos</p>
        </div>

        {loadError && (
          <p className="info-banner" style={{ marginBottom: '1rem', color: '#666' }}>
            Não foi possível atualizar pela API. Exibindo dados de exemplo ou última lista carregada.
          </p>
        )}

        {/* Resumo */}
        <div className="summary-cards">
          <div className="summary-card">
            <h3>Total Pago</h3>
            <span className="value">R$ 176,00</span>
            <p>Últimos 3 meses</p>
          </div>
          <div className="summary-card">
            <h3>Próximo Vencimento</h3>
            <span className="value">R$ 88,00</span>
            <p>15/03/2025</p>
          </div>
          <div className="summary-card">
            <h3>Status</h3>
            <span className="value status-good">Em dia</span>
            <p>Nenhuma fatura vencida</p>
          </div>
        </div>

        {/* Lista de Faturas */}
        <div className="faturas-list">
          <div className="list-header">
            <h2>Histórico de Faturas</h2>
            <div className="filters">
              <select className="filter-select">
                <option value="">Todos os status</option>
                <option value="pago">Pago</option>
                <option value="pendente">Pendente</option>
                <option value="vencido">Vencido</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div className="loading">Carregando faturas…</div>
          ) : (
            <div className="faturas-table">
              <table>
                <thead>
                  <tr>
                    <th>Referência</th>
                    <th>Número</th>
                    <th>Vencimento</th>
                    <th>Valor</th>
                    <th>Pagamento</th>
                    <th>Status</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {faturas.map((fatura) => (
                    <tr key={fatura.id}>
                      <td>{fatura.referencia || fatura.mes}</td>
                      <td>{fatura.numero || fatura.codigo || '—'}</td>
                      <td>{fatura.vencimento}</td>
                      <td>R$ {Number(fatura.valor || 0).toFixed(2)}</td>
                      <td>
                        {fatura.status === 'Pago' && fatura.dataPagamento ? (
                          <span style={{ color: '#1a7f37', fontWeight: 600 }}>
                            R$ {Number(fatura.valor || 0).toFixed(2)} · {fatura.dataPagamento}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        <span
                          className={`status-badge ${getStatusClass(
                            fatura.status,
                            fatura.vencimento
                          )}`}
                        >
                          {getStatusText(fatura.status, fatura.vencimento)}
                        </span>
                      </td>
                      <td>
                        <div className="actions">
                          <button className="btn-action" type="button" title="Visualizar">
                            <i className="fas fa-eye"></i>
                          </button>
                          <button
                            className="btn-action"
                            type="button"
                            title={
                              boletoKey(fatura)
                                ? 'Baixar boleto em PDF'
                                : 'Boleto indisponível (sem número/chave)'
                            }
                            disabled={!boletoKey(fatura)}
                            onClick={() => handleDownloadBoleto(fatura)}
                          >
                            <i className="fas fa-download"></i>
                          </button>
                          {(fatura.status === 'Pendente' ||
                            getStatusText(fatura.status, fatura.vencimento) === 'Vencido') && (
                            <button
                              className="btn-action primary"
                              type="button"
                              title="Pagar"
                              onClick={() => handlePaymentClick(fatura)}
                            >
                              <i className="fas fa-credit-card"></i>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Informações Adicionais */}
        <div className="info-section">
          <div className="info-card">
            <h3>Formas de Pagamento</h3>
            <ul>
              <li>
                <i className="fas fa-credit-card"></i> Cartão de Crédito
              </li>
              <li>
                <i className="fas fa-university"></i> PIX
              </li>
              <li>
                <i className="fas fa-barcode"></i> Boleto Bancário
              </li>
              <li>
                <i className="fas fa-mobile-alt"></i> Débito Automático
              </li>
            </ul>
          </div>

          <div className="info-card">
            <h3>Precisa de Ajuda?</h3>
            <p>Entre em contato conosco para esclarecer dúvidas sobre suas faturas.</p>
            <div className="contact-actions">
              <Link to="/central/suporte" className="btn-secondary">
                Abrir Chamado
              </Link>
              <a
                href="https://wa.me/5522999611338"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-whatsapp"
              >
                <i className="fab fa-whatsapp"></i> WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>

      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        fatura={selectedFatura}
        onDownloadBoleto={handleDownloadBoleto}
      />
    </div>
  );
}

export default Faturas;
